
import { useI18n } from 'vue-i18n';
import PageHeader from '@/components/layout/PageHeader.vue';
import {
  defineComponent,
  onBeforeUnmount,
  onMounted,
  ref,
} from 'vue';

import Button from 'primevue/button';

import SpmTableAPI from '@/components/table-rest-api/SpmTableAPI.vue';
import type {
  SpmColumnConfig,
  ApiCallConfig,
  RowActionOption,
  SpmApiFilter,
} from '@/components/table-rest-api/types';
import { NestApiType } from '@/composables/nestApi';
import { SpmFilterOperator } from '@/components/table-rest-api/filters';
import { ShopsSendDomains } from '@/types/generated-types/graphql';
import { findPermission, UserState } from '@/composables/User';
import DomainInstructions from '@/views/shop/DomainInstructions.vue';
import CreateDomain from '@/components/shop/my-domains/CreateDomain.vue';
import DomainOwnershipValidation from '@/components/shop/my-domains/DomainOwnershipValidation.vue';
import { useConfirm } from 'primevue/useconfirm';
import { DeleteShopsSendDomains, ValidateShopDomainByToken, VerifyDomain } from '@/composables/shop/ShopsSendDomains';
import { showToastError, showToastSuccess } from '@/helpers';
import ConfirmDialog from 'primevue/confirmdialog';
import Message from 'primevue/message';
import ActionRenderer from '@/components/table/renderers/ActionRenderer.vue';
import type { SpmTableAction } from '@/types';

// @see class Menu in node_modules/primevue/menu/Menu.d.ts
interface MenuInterface {
  toggle(event: Event): void;
}

export default defineComponent({
  name: 'MyDomainsV2',
  components: {
    SpmTableAPI,
    PageHeader,
    ActionRenderer,
    CreateDomain,
    DomainInstructions,
    DomainOwnershipValidation,
    Message,
    ConfirmDialog,
    Button,
  },

  props: {
    token: {
      type: String,
      required: false,
      default: undefined,
    },
  },

  setup(props) {
    const { t } = useI18n();
    const confirm = useConfirm();
    const idShop = UserState.activeShop?.id;
    const locale = UserState.user.lang;
    const domainValid = ref(false);
    const showMessage = ref(false);
    const childComponentRef = ref();
    const verifyingDns = ref<Set<number>>(new Set());
    const pendingDomainChecks = ref<Map<number, number>>(new Map());
    const domainRowMap = ref<Map<number, ShopsSendDomains>>(new Map());
    const pollTimer = ref<number | null>(null);
    const POLLING_INTERVAL_MS = 10000;
    const MAX_POLL_ATTEMPTS = 100;
    const TOTAL_STEPS = 3;

    // API Call configuration for SpmTableAPI
    const apiCall = ref<ApiCallConfig>({
      type_api: NestApiType.CLIENT_API,
      path: '/domains',
      secure: true,
    });

    // Persistent filters as a separate prop
    const persistentFilters = ref<SpmApiFilter[]>([
      {
        field: 'id_shop',
        operator: SpmFilterOperator.EQUALS,
        values: idShop ?? 0,
      },
    ]);

    type StepState = 'done' | 'progress' | 'todo' | 'warning';
    type WeatherState = 'storm' | 'rain' | 'clouds' | 'sun';

    interface StepActionConfig {
      label: string;
      variant: string;
      onClick: () => void;
    }

    interface StepDefinition {
      key: string;
      label: string;
      description: string;
      icon: string;
      state: StepState;
      actions: StepActionConfig[];
      weatherIcon: string;
    }

    const weatherIcons: Record<WeatherState, string> = {
      storm: 'fa-solid fa-cloud-bolt',
      rain: 'fa-solid fa-cloud-rain',
      clouds: 'fa-solid fa-cloud-sun',
      sun: 'fa-solid fa-sun',
    };

    const stepWeatherIcons: Record<StepState, string> = {
      done: 'fa-solid fa-sun',
      progress: 'fa-solid fa-cloud-sun',
      todo: 'fa-solid fa-cloud',
      warning: 'fa-solid fa-triangle-exclamation',
    };

    const displayDialog = ref<boolean>(false);
    const sendDomainsData = ref<ShopsSendDomains>();
    const type = ref('');

    const showDialog = (data: ShopsSendDomains, dialogType: string) => {
      displayDialog.value = true;
      sendDomainsData.value = data;
      type.value = dialogType;
    };

    // Helper function to check if a value is verified (handles string/boolean/number)
    // New API returns '0' or '1' as strings, old API returned boolean
    // Only value 1 is considered as verified
    const isVerified = (value: any): boolean => {
      if (typeof value === 'string') {
        return value === '1' || value === 'true';
      }
      return value === true || value === 1;
    };

    // alignment_verified: 0 = not verified, 1 = default subdomain configured, 2 = custom subdomain configured
    // Both 1 and 2 represent a valid alignment.
    const isAlignmentVerified = (value: any): boolean => {
      const num = typeof value === 'string' ? Number(value) : value;
      return num === 1 || num === 2 || num === true;
    };

    // Helper function to check if a value is in temporary invalid state (2, 3, 4)
    // These values represent temporary invalidity and should show a warning icon
    const isTemporaryInvalid = (value: any): boolean => {
      if (typeof value === 'string') {
        return value === '2' || value === '3' || value === '4';
      }
      return value === 2 || value === 3 || value === 4;
    };

    const getCompletedSteps = (domain: any): number => {
      let completed = 0;

      if (isVerified(domain.authentication_verified)) completed += 1;
      if (isAlignmentVerified(domain.alignment_verified)) completed += 1;
      if (isVerified(domain.tracking_domain_verified)) completed += 1;

      return completed;
    };

    const getDeliverabilityScore = (domain: ShopsSendDomains): number => {
      const completed = getCompletedSteps(domain);

      return Math.round((completed / TOTAL_STEPS) * 100);
    };

    const getScoreLevel = (domain: ShopsSendDomains): 'critical' | 'warning' | 'success' => {
      const score = getDeliverabilityScore(domain);
      if (score === 0) return 'critical';
      if (score === 100) return 'success';
      return 'warning';
    };

    const getWeatherState = (domain: ShopsSendDomains): WeatherState => {
      const completed = getCompletedSteps(domain);

      if (completed >= 3) {
        return 'sun';
      }

      if (completed === 2) {
        return 'clouds';
      }

      if (completed === 1) {
        return 'rain';
      }

      return 'storm';
    };

    const getWeatherIcon = (domain: ShopsSendDomains): string => weatherIcons[getWeatherState(domain)];

    const getWeatherLabel = (domain: ShopsSendDomains): string => t(`shop.my_domains.deliverability.weather.${getWeatherState(domain)}`);

    const buildAuthenticationActions = (domain: ShopsSendDomains, state: StepState): StepActionConfig[] => {
      if (state === 'done') {
        return [{
          label: t('shop.my_domains.deliverability.actions.review'),
          variant: 'p-button-secondary',
          onClick: () => showDialog(domain, 'authenticationInstructionNewMethod'),
        }];
      }

      if (state === 'progress') {
        return [{
          label: t('shop.my_domains.deliverability.actions.resume'),
          variant: 'p-button-warning',
          onClick: () => showDialog(domain, 'authenticationInstructionNewMethod'),
        }];
      }

      return [{
        label: t('shop.my_domains.deliverability.actions.start'),
        variant: 'p-button-success',
        onClick: () => showDialog(domain, 'authenticationInstruction'),
      }];
    };

    const buildAlignmentActions = (domain: ShopsSendDomains, state: StepState): StepActionConfig[] => {
      if (state === 'done') {
        return [{
          label: t('shop.my_domains.modify'),
          variant: 'p-button-secondary',
          onClick: () => showDialog(domain, 'alignmentInstruction'),
        }];
      }

      return [{
        label: t('shop.my_domains.deliverability.actions.start'),
        variant: 'p-button-success',
        onClick: () => showDialog(domain, 'alignmentInstruction'),
      }];
    };

    const buildTrackingActions = (domain: ShopsSendDomains, state: StepState): StepActionConfig[] => {
      if (state === 'done') {
        return [{
          label: t('shop.my_domains.modify'),
          variant: 'p-button-secondary',
          onClick: () => showDialog(domain, 'trackingDomainInstruction'),
        }];
      }

      return [{
        label: t('shop.my_domains.deliverability.actions.start'),
        variant: 'p-button-success',
        onClick: () => showDialog(domain, 'trackingDomainInstruction'),
      }];
    };

    const getAuthenticationState = (domain: any): StepState => {
      if (isVerified(domain.authentication_verified)) {
        return 'done';
      }

      if (isTemporaryInvalid(domain.authentication_verified)) {
        return 'warning';
      }

      if (isVerified(domain.dkim_verified) && domain.public_key !== null) {
        return 'progress';
      }

      return 'todo';
    };

    const getAlignmentState = (domain: any): StepState => {
      if (isAlignmentVerified(domain.alignment_verified)) {
        return 'done';
      }
      return 'todo';
    };

    const getTrackingState = (domain: any): StepState => {
      if (isVerified(domain.tracking_domain_verified)) {
        return 'done';
      }

      if (isTemporaryInvalid(domain.tracking_domain_verified)) {
        return 'warning';
      }

      return 'todo';
    };

    const getSteps = (domain: ShopsSendDomains): StepDefinition[] => {
      const authenticationState = getAuthenticationState(domain);
      const alignmentState = getAlignmentState(domain);
      const trackingState = getTrackingState(domain);

      return [
        {
          key: 'authentication',
          label: t('shop.my_domains.deliverability.steps.authentication.title'),
          description: t('shop.my_domains.deliverability.steps.authentication.desc'),
          icon: 'fa-solid fa-shield-halved',
          state: authenticationState,
          actions: buildAuthenticationActions(domain, authenticationState),
          weatherIcon: stepWeatherIcons[authenticationState],
        },
        {
          key: 'alignment',
          label: t('shop.my_domains.deliverability.steps.alignment.title'),
          description: t('shop.my_domains.deliverability.steps.alignment.desc'),
          icon: 'fa-solid fa-pen-ruler',
          state: alignmentState,
          actions: buildAlignmentActions(domain, alignmentState),
          weatherIcon: stepWeatherIcons[alignmentState],
        },
        {
          key: 'tracking',
          label: t('shop.my_domains.deliverability.steps.tracking.title'),
          description: t('shop.my_domains.deliverability.steps.tracking.desc'),
          icon: 'fa-solid fa-compass-drafting',
          state: trackingState,
          actions: buildTrackingActions(domain, trackingState),
          weatherIcon: stepWeatherIcons[trackingState],
        },
      ];
    };

    const stopPolling = () => {
      if (pollTimer.value !== null) {
        window.clearTimeout(pollTimer.value);
        pollTimer.value = null;
      }
    };

    const normalizeDomainId = (domain: ShopsSendDomains): number | null => {
      if (typeof domain.id_domain === 'number') {
        return domain.id_domain;
      }

      if (typeof domain.id_domain === 'string') {
        const parsedId = Number(domain.id_domain);

        if (!Number.isNaN(parsedId)) {
          return parsedId;
        }
      }

      return null;
    };

    const updateDomainRowMap = (rows: ShopsSendDomains[]) => {
      const seenIds = new Set<number>();

      rows.forEach((row) => {
        const domainId = normalizeDomainId(row);

        if (domainId === null) {
          return;
        }

        domainRowMap.value.set(domainId, row);
        seenIds.add(domainId);
      });

      [...domainRowMap.value.keys()].forEach((domainId) => {
        if (!seenIds.has(domainId)) {
          domainRowMap.value.delete(domainId);
          pendingDomainChecks.value.delete(domainId);
          verifyingDns.value.delete(domainId);
        }
      });
    };

    const needsPolling = (domain: ShopsSendDomains): boolean => {
      if (!isVerified(domain.domain_verified)) return true;
      if (!isVerified(domain.authentication_verified)) return true;
      if (domain.alignment_domain && !isAlignmentVerified(domain.alignment_verified)) return true;
      if (domain.tracking_domain && !isVerified(domain.tracking_domain_verified)) return true;
      return false;
    };

    const isFullyConfigured = (domain: ShopsSendDomains): boolean => !needsPolling(domain);

    const registerPendingDomains = (domains: ShopsSendDomains[]) => {
      const currentPendingIds = new Set<number>();

      console.log('[POLL] registerPendingDomains called with', domains.length, 'domains');

      domains.forEach((domain) => {
        const domainId = normalizeDomainId(domain);

        if (domainId === null) {
          console.log('[POLL] skip - domainId null', domain);
          return;
        }

        const fullyConfigured = isFullyConfigured(domain);
        console.log('[POLL] domain', domainId, 'fullyConfigured:', fullyConfigured, {
          domain_verified: domain.domain_verified,
          authentication_verified: domain.authentication_verified,
          alignment_verified: domain.alignment_verified,
          tracking_domain_verified: domain.tracking_domain_verified,
        });

        if (!fullyConfigured) {
          currentPendingIds.add(domainId);

          if (!pendingDomainChecks.value.has(domainId)) {
            pendingDomainChecks.value.set(domainId, 0);
            console.log('[POLL] added to pending:', domainId);
          }
        } else {
          pendingDomainChecks.value.delete(domainId);
        }
      });

      [...pendingDomainChecks.value.keys()].forEach((domainId) => {
        if (!currentPendingIds.has(domainId)) {
          pendingDomainChecks.value.delete(domainId);
        }
      });

      console.log('[POLL] pendingDomainChecks size after register:', pendingDomainChecks.value.size);

      if (pendingDomainChecks.value.size === 0) {
        console.log('[POLL] no pending domains, stopping');
        stopPolling();
      }
    };

    const incrementPollAttempts = () => {
      const attemptsMap = pendingDomainChecks.value;

      [...attemptsMap.keys()].forEach((domainId) => {
        const attempts = attemptsMap.get(domainId) ?? 0;
        const nextAttempt = attempts + 1;

        if (nextAttempt >= MAX_POLL_ATTEMPTS) {
          attemptsMap.delete(domainId);
        } else {
          attemptsMap.set(domainId, nextAttempt);
        }
      });

      if (attemptsMap.size === 0) {
        stopPolling();
        return false;
      }

      return true;
    };

    const applyDomainVerificationUpdates = (
      domainId: number,
      updates: Pick<ShopsSendDomains,
      'domain_verified' | 'authentication_verified' | 'alignment_verified' | 'tracking_domain_verified'>,
    ) => {
      const targetDomain = domainRowMap.value.get(domainId);

      if (!targetDomain) {
        return false;
      }

      const previousState = {
        domain_verified: targetDomain.domain_verified,
        authentication_verified: targetDomain.authentication_verified,
        alignment_verified: targetDomain.alignment_verified,
        tracking_domain_verified: targetDomain.tracking_domain_verified,
      };

      Object.assign(targetDomain, updates);

      if (isFullyConfigured(targetDomain)) {
        pendingDomainChecks.value.delete(domainId);
      }

      return previousState.domain_verified !== targetDomain.domain_verified
        || previousState.authentication_verified !== targetDomain.authentication_verified
        || previousState.alignment_verified !== targetDomain.alignment_verified
        || previousState.tracking_domain_verified !== targetDomain.tracking_domain_verified;
    };

    const verifyDomains = async (domains: ShopsSendDomains[]) => {
      console.log('[POLL] verifyDomains called for', domains.length, 'domains');
      const verificationTasks = domains.map(async (domain) => {
        const domainId = normalizeDomainId(domain);
        console.log('[POLL] verifying domainId', domainId);
        if (domainId === null) {
          return false;
        }

        verifyingDns.value.add(domainId);

        try {
          console.log('[POLL] calling VerifyDomain API for', domainId);
          const { data, err } = await VerifyDomain(domainId);
          console.log('[POLL] VerifyDomain returned', { domainId, data, err });

          if (err || !data) {
            return false;
          }

          return applyDomainVerificationUpdates(domainId, {
            domain_verified: data.domain_verified,
            authentication_verified: data.authentication_verified,
            alignment_verified: data.alignment_verified,
            tracking_domain_verified: data.tracking_domain_verified,
          });
        } catch (error) {
          console.error('Failed to auto-verify domain DNS', error);
        } finally {
          verifyingDns.value.delete(domainId);
        }

        return false;
      });

      await Promise.all(verificationTasks);
    };

    const schedulePoll = () => {
      console.log('[POLL] schedulePoll called. timer:', pollTimer.value, 'pending size:', pendingDomainChecks.value.size);
      if (pollTimer.value !== null || pendingDomainChecks.value.size === 0) {
        console.log('[POLL] schedulePoll early return');
        return;
      }

      console.log('[POLL] setting timer for', POLLING_INTERVAL_MS, 'ms');
      pollTimer.value = window.setTimeout(async () => {
        console.log('[POLL] timer fired');
        pollTimer.value = null;

        const shouldContinue = incrementPollAttempts();

        if (!shouldContinue) {
          console.log('[POLL] max attempts reached, stopping');
          return;
        }

        const pendingIds = [...pendingDomainChecks.value.keys()]
          .filter((domainId) => !verifyingDns.value.has(domainId));

        const domainsToVerify = pendingIds
          .map((domainId) => domainRowMap.value.get(domainId))
          .filter((domain): domain is ShopsSendDomains => Boolean(domain));

        console.log('[POLL] going to verify', domainsToVerify.length, 'domains');

        if (domainsToVerify.length > 0) {
          await verifyDomains(domainsToVerify);
        }

        schedulePoll();
      }, POLLING_INTERVAL_MS);
    };
    const actions: RowActionOption[] = [
      {
        label: t('shop.my_domains.delete.actionText'),
        icon: 'far fa-trash',
        command: (data: ShopsSendDomains) => {
          confirm.require({
            message: t('shop.my_domains.delete.confirmText'),
            header: t('shop.my_domains.delete.confirmHeader'),
            icon: 'fa-regular fa-square-info',
            acceptClass: 'p-button-danger',
            accept: async () => {
              const result = await DeleteShopsSendDomains([{ id_domain: data.id_domain }]);

              if (result.status) {
                await showToastSuccess('shop.my_domains.delete.success');
                await childComponentRef.value?.reloadData();
              } else {
                await showToastError('shop.my_domains.delete.error');
              }
            },
          });
        },

        visible: () => findPermission('my_domains.delete'),
      },
    ];

    // Actions au format SpmTableAction pour ActionRenderer
    const actionsForRenderer: SpmTableAction[] = [
      {
        label: t('shop.my_domains.delete.actionText'),
        icon: 'far fa-trash',
        command: (data: ShopsSendDomains) => {
          confirm.require({
            message: t('shop.my_domains.delete.confirmText'),
            header: t('shop.my_domains.delete.confirmHeader'),
            icon: 'fa-regular fa-square-info',
            acceptClass: 'p-button-danger',
            accept: async () => {
              const result = await DeleteShopsSendDomains([{ id_domain: data.id_domain }]);

              if (result.status) {
                showToastSuccess('shop.my_domains.delete.success');
                await childComponentRef.value?.reloadData();
              } else {
                showToastError('shop.my_domains.delete.error');
              }
            },
          });
        },

        show: () => findPermission('my_domains.delete'),
      },
    ];

    const columns = ref<SpmColumnConfig[]>([
      {
        id: 'domain',
        header: t('shop.my_domains.headers.domain.title'),
        fields: [{
          name: 'domain',
          label: t('shop.my_domains.headers.domain.title'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'textInput',
          },
        }],
      },
      {
        id: 'domain_verified',
        header: t('shop.my_domains.headers.verification.title'),
        fields: [{
          name: 'domain_verified',
          label: t('shop.my_domains.headers.verification.title'),
          sortable: true,
          filterable: true,
        }],
        hidden: true,
      },
      {
        id: 'authentication_verified',
        header: t('shop.my_domains.headers.authentication.title'),
        fields: [{
          name: 'authentication_verified',
          label: t('shop.my_domains.headers.authentication.title'),
          sortable: true,
          filterable: true,
        }],
        hidden: true,
      },
      {
        id: 'alignment_verified',
        header: t('shop.my_domains.headers.alignment.title'),
        fields: [{
          name: 'alignment_verified',
          label: t('shop.my_domains.headers.alignment.title'),
          sortable: true,
          filterable: true,
        }],
        hidden: true,
      },
      {
        id: 'tracking_domain_verified',
        header: t('shop.my_domains.headers.tracking.title'),
        fields: [{
          name: 'tracking_domain_verified',
          label: t('shop.my_domains.headers.tracking.title'),
          sortable: true,
          filterable: true,
        }],
        hidden: true,
      },
      {
        id: 'dkim_verified',
        header: t('shop.my_domains.headers.dkim_verified.title'),
        fields: [{
          name: 'dkim_verified',
          label: t('shop.my_domains.headers.dkim_verified.title'),
          sortable: true,
          filterable: true,
        }],
        hidden: true,
      },
      {
        id: 'spf_verified',
        header: t('shop.my_domains.headers.spf_verified.title'),
        fields: [{
          name: 'spf_verified',
          label: t('shop.my_domains.headers.spf_verified.title'),
          sortable: true,
          filterable: true,
        }],
        hidden: true,
      },
      {
        id: 'dkim_selector',
        header: t('shop.my_domains.headers.dkim_selector.title'),
        fields: [{
          name: 'dkim_selector',
          label: t('shop.my_domains.headers.dkim_selector.title'),
          sortable: true,
          filterable: true,
        }],
        hidden: true,
      },
      {
        id: 'public_key',
        header: t('shop.my_domains.headers.public_key.title'),
        fields: [{
          name: 'public_key',
          label: t('shop.my_domains.headers.public_key.title'),
          sortable: true,
          filterable: true,
        }],
        hidden: true,
      },
      {
        id: 'id_shop',
        header: t('shop.my_domains.headers.id_shop.title'),
        fields: [{
          name: 'id_shop',
          label: t('shop.my_domains.headers.id_shop.title'),
          sortable: true,
          filterable: true,
        }],
        hidden: true,
      },
      {
        id: 'tracking_domain',
        header: t('shop.my_domains.headers.tracking_domain.title'),
        fields: [{
          name: 'tracking_domain',
          label: t('shop.my_domains.headers.tracking_domain.title'),
          sortable: true,
          filterable: true,
        }],
        hidden: true,
      },
      {
        id: 'tracking_domain_default_medias',
        header: t('shop.my_domains.headers.tracking_domain_default_medias.title'),
        fields: [{
          name: 'tracking_domain_default_medias',
          label: t('shop.my_domains.headers.tracking_domain_default_medias.title'),
          sortable: true,
          filterable: true,
        }],
        hidden: true,
      },
      {
        id: 'id_domain',
        header: t('shop.my_domains.headers.id_domain.title'),
        fields: [{
          name: 'id_domain',
          label: t('shop.my_domains.headers.id_domain.title'),
          sortable: true,
          filterable: true,
        }],
        hidden: true,
      },
      {
        id: 'dns_verification_key',
        header: t('shop.my_domains.headers.dns_verification_key.title'),
        fields: [{
          name: 'dns_verification_key',
          label: t('shop.my_domains.headers.dns_verification_key.title'),
          sortable: true,
          filterable: true,
        }],
        hidden: true,
      },
      {
        id: 'alignment_domain',
        header: t('shop.my_domains.headers.alignment_domain.title'),
        fields: [{
          name: 'alignment_domain',
          label: t('shop.my_domains.headers.alignment_domain.title'),
          sortable: true,
          filterable: true,
        }],
        hidden: true,
      },
      {
        id: 'created_at',
        header: t('shop.my_domains.headers.created_at.title'),
        fields: [{
          name: 'created_at',
          label: t('shop.my_domains.headers.created_at.title'),
          sortable: true,
          filterable: true,
        }],
        hidden: true,
      },
      {
        id: 'updated_at',
        header: t('shop.my_domains.headers.updated_at.title'),
        fields: [{
          name: 'updated_at',
          label: t('shop.my_domains.headers.updated_at.title'),
          sortable: true,
          filterable: true,
        }],
        hidden: true,
      },
    ]);

    const closeDialog = (refresh: boolean) => {
      displayDialog.value = false;
    };

    const handleDomainValidated = async () => {
      await childComponentRef.value?.reloadData();
    };

    const handleDataLoaded = async (rows: ShopsSendDomains[]) => {
      console.log('[POLL] handleDataLoaded called with', rows?.length, 'rows', rows);
      if (!Array.isArray(rows) || rows.length === 0) {
        console.log('[POLL] handleDataLoaded - no data');
        return;
      }

      updateDomainRowMap(rows);

      registerPendingDomains(rows);

      const pendingVerification = rows.filter((row) => {
        const domainId = normalizeDomainId(row);

        return domainId !== null
          && !isFullyConfigured(row)
          && !verifyingDns.value.has(domainId);
      });

      if (pendingVerification.length > 0) {
        await verifyDomains(pendingVerification);
      }

      schedulePoll();
    };

    onMounted(async () => {
      if (props.token && props.token !== '') {
        const result = await ValidateShopDomainByToken(props.token, idShop ?? 0);

        if (result.status && result.id) {
          domainValid.value = true;
          await childComponentRef.value?.reloadData();
        }

        showMessage.value = true;
        setTimeout(() => {
          showMessage.value = false;
        }, 5000);
      }
    });

    onBeforeUnmount(() => {
      stopPolling();
      pendingDomainChecks.value.clear();
      verifyingDns.value.clear();
      domainRowMap.value.clear();
    });

    return {
      t,
      columns,
      apiCall,
      persistentFilters,
      findPermission,
      actionsForRenderer,
      sendDomainsData,
      displayDialog,
      type,
      closeDialog,
      domainValid,
      showMessage,
      locale,
      childComponentRef,
      handleDataLoaded,
      handleDomainValidated,
      isVerified,
      getDeliverabilityScore,
      getScoreLevel,
      getWeatherIcon,
      getWeatherLabel,
      getWeatherState,
      getSteps,
      totalSteps: TOTAL_STEPS,
    };
  },
});
